import { registerPlugin } from '@capacitor/core'
import { isNativePlatform } from '@/platform/platform'

export interface WebBundleManifest {
  version: string
  createdAt: string
  zipUrl: string
  sha256: string
  sizeBytes: number
  minimumNativeVersion: string
  minimumApiVersion: string
  minimumLocalSchemaVersion: number
  notes?: string
}

export interface UpdateStatus {
  builtinVersion: string
  currentVersion: string
  previousVersion: string | null
  pendingVersion: string | null
  probation: boolean
  manifestUrl: string
}

interface WebBundleUpdaterPlugin {
  getStatus(): Promise<UpdateStatus>
  checkForUpdate(): Promise<{ available: boolean; manifest?: WebBundleManifest; reason?: string }>
  downloadAndInstall(): Promise<{ installedVersion: string }>
  markHealthy(): Promise<void>
  rollback(): Promise<{ activeVersion: string }>
  resetToBuiltIn(): Promise<void>
}

const WebBundleUpdater = registerPlugin<WebBundleUpdaterPlugin>('WebBundleUpdater')

// Web OTA update manager. Only meaningful inside the Android shell; on web the
// PWA service worker owns updates, so every method degrades gracefully.
export const updateManager = {
  isSupported: () => isNativePlatform(),
  async getStatus(): Promise<UpdateStatus | null> {
    if (!isNativePlatform()) return null
    return WebBundleUpdater.getStatus()
  },
  async checkForUpdate() {
    if (!isNativePlatform()) return { available: false as const, reason: 'web 平台由 PWA 自动更新' }
    return WebBundleUpdater.checkForUpdate()
  },
  async downloadAndInstall() {
    if (!isNativePlatform()) throw new Error('仅 Android App 支持热更新安装')
    return WebBundleUpdater.downloadAndInstall()
  },
  async markHealthy() {
    if (!isNativePlatform()) return
    try {
      await WebBundleUpdater.markHealthy()
    } catch (error) {
      console.warn('WebBundleUpdater markHealthy failed', error)
    }
  },
  async rollback() {
    if (!isNativePlatform()) throw new Error('仅 Android App 支持回滚')
    return WebBundleUpdater.rollback()
  },
  async resetToBuiltIn() {
    if (!isNativePlatform()) throw new Error('仅 Android App 支持恢复内置版本')
    return WebBundleUpdater.resetToBuiltIn()
  },
}
